import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import {
  type ColDef,
  type CellClassParams,
  type ValueGetterParams,
  type HeaderClassParams,
  type HeaderValueGetterParams,
  type GetMainMenuItemsParams,
  type MenuItemDef,
} from 'ag-grid-community';

import {
  getAgTableHeaderName,
  getAlignStyle,
  getCustomCellStyle,
  getCustomHeaderStyle,
} from '../helpers/TableColumnFormatting';
import { getFieldName } from '../helpers/getFieldName';
import { GroupAndTotalRenderer } from '../GroupAndTotalRenderer';
import { I18n } from 'common/i18n';
import { GROUP_COLUMN_PREFIX, NULL_GROUPING } from '../Constants';
import { getPinColumnMenuItem } from '../helpers/getPinColumnMenuItem';
import { isColumnPinningEnabled } from '../helpers/isColumnPinningEnabled';
import { type CustomAgGridContext } from 'common/types/agGrid/context';
import { generateCellClasses } from 'common/visualizations/views/agGridReact/helpers/TableExportFormatting';
import getCustomSortMenuItems from '../helpers/getCustomSortMenuItems';
import { type ViewColumn } from 'common/types/viewColumn';
import {
  type CustomLabels,
  type CustomSortConfig,
  type Hierarchy,
  type HierarchyColumnConfig,
  type OrderConfig,
} from 'common/visualizations/vif';
import { type UniqueValuesData, type TableColumnFormat, type HeaderFormatStyle } from '../types';
import { tooltipValueGetterFunction } from '../helpers/ColDefHelpers';
import { isTotalCell, isSubtotalCell } from '../helpers/isTotalCell';
import { isTableAdvancedTotalingEnabled } from 'common/visualizations/helpers/VifSelectors';
import isUndefined from 'lodash/isUndefined';
import isBlank from 'common/js_utils/isBlank';
import 'common/visualizations/views/agGridReact/customHooks/useAutoGroupAttributes.scss';

const scope = 'shared.visualizations.charts';

const noValueText = I18n.t('common.summary_table.no_value', { scope });
const totalText = I18n.t('table.total', { scope });

export interface AutoGroupAttributesProps {
  datasetUid: string;
  domain: string;
  isIndented?: boolean;
  singleAutoColumnWidth?: number;
  shouldGenerateExportCellClasses?: boolean;
  shouldGenerateGridClasses?: boolean;
  showCustomSortMenuItems?: boolean;
  mainMenuItems?: (string | MenuItemDef)[];
  columns?: ViewColumn[];
  setIsCustomSortModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedColumns?: ViewColumn[];
  setSelectedColumns?: React.Dispatch<React.SetStateAction<ViewColumn[]>>;
  hierarchies?: HierarchyColumnConfig[];
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
    limit?: number,
  ) => Promise<UniqueValuesData>;
  vifOrderConfig: OrderConfig[];
  columnFormats: { [key: string]: TableColumnFormat };
  hierarchyConfig: Hierarchy | undefined;
  setCurrentSortState?: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle?: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleGroupColumnPinnedChange?: (colId: string, pinned: boolean, hierarchyId?: string) => void;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
  headerFormat: HeaderFormatStyle;
}

const getHeaderValue = (params: HeaderValueGetterParams, isIndented: boolean | undefined) => {
  const { columnMetadata, columnFormats } = params.context as CustomAgGridContext;
  let fieldName = '';
  if (isIndented) {
    fieldName = params?.api.getRowGroupColumns()[0].getColDef().field ?? '';
  } else {
    // params.colDef can be a single column or a row group, but the type doesn't include specific fields from either
    // @ts-expect-error TS(2339) FIXME: Property 'field' does not exist on type 'AbstractC... Remove this comment to see the full error message
    fieldName = params?.colDef?.field || params?.colDef?.showRowGroup;
  }

  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata) {
    return params.colDef.headerName;
  }
  return getAgTableHeaderName(columnFormats[fieldName], localColumnMetadata);
};

export const getHeaderClass = (params: HeaderClassParams, isIndented: boolean | undefined) => {
  const { columnMetadata, columnFormats, headerFormat } = params.context as CustomAgGridContext;
  let fieldName = '';
  if (isIndented) {
    // indented renders one merged auto-group column with no field; resolve it from the first row group column (like getHeaderValue).
    fieldName = params?.api?.getRowGroupColumns()?.[0]?.getColDef().field ?? '';
  } else {
    // params.colDef can be a single column or a row group, but the type doesn't include specific fields from either
    // @ts-expect-error TS(2339) FIXME: Property 'field' does not exist on type 'AbstractC... Remove this comment to see the full error message
    fieldName = params?.colDef?.field || params?.colDef?.showRowGroup;
  }
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata) {
    return '';
  }
  const customHeaderStyle = getCustomHeaderStyle(columnFormats[fieldName]);
  if (!isEmpty(customHeaderStyle)) {
    return [customHeaderStyle, 'ag-grid-header'];
  }

  // fall back to the global Presentation > Headers alignment, mirroring headerClassFunction.
  const globalHeaderAlignment = headerFormat?.alignHeaders;
  if (globalHeaderAlignment && !isEmpty(globalHeaderAlignment)) {
    return [getAlignStyle(globalHeaderAlignment), 'ag-grid-header'];
  }

  return ['ag-grid-header'];
};

const getValue = (params: ValueGetterParams, isIndented?: boolean, customLabels?: CustomLabels) => {
  // If we have a total row we want the first grouped column to have "Total" as the value
  const columnDefs: ColDef[] | undefined = params?.api?.getColumnDefs();
  if (!columnDefs) {
    return;
  }

  let totalReturnText = totalText;

  if (isTableAdvancedTotalingEnabled()) {
    const customLabel = customLabels?.totals?.trim();
    if (!isUndefined(customLabels?.totals) && !isBlank(customLabel)) {
      totalReturnText = customLabel;
    }
  }

  const fieldName = getFieldName(params);
  if (isTotalCell(params)) {
    if (isIndented) {
      return totalReturnText;
    }
    const firstGroupedColumn = find(columnDefs, ['rowGroupIndex', 0]);
    const firstGroupedColumnField = firstGroupedColumn?.field;
    if (fieldName === firstGroupedColumnField) {
      return totalReturnText;
    }
  }

  const nodeField = params.node?.field;
  if (params.node?.footer && fieldName && fieldName === nodeField) {
    const value = params.getValue(fieldName);
    return value == NULL_GROUPING ? noValueText : value;
  }
};

const getCellClass = (params: CellClassParams) => {
  const fieldName = getFieldName(params) ?? '';
  const column = find(params.context.columnMetadata, { fieldName: fieldName });
  // params.node.key contains the true/unformatted value
  // whereas params.value contains the formatted value from the renderer (exportHooks - processCellCallback).
  const dataValue = params.node.key;
  const value = dataValue || params.value;
  const cellClassesParams = {
    colFieldName: fieldName,
    colDataTypeName: column?.dataTypeName ?? '',
    node: params.node,
    context: params.context,
    value: value,
    colDef: params.colDef,
    api: params.api,
  };
  return generateCellClasses(cellClassesParams);
};

export const shouldApplyStyleOptions = (params: CellClassParams, columnFormat: TableColumnFormat) => {
  if (isTotalCell(params)) {
    return columnFormat.isFormatTotal ?? false;
  } else if (isSubtotalCell(params)) {
    return columnFormat.isFormatSubtotal ?? false;
  } else {
    return columnFormat.isFormatValue ?? true;
  }
};

export const shouldApplyCellAlignmentClass = (
  params: CellClassParams,
  rule: string,
  scopeClass?: 'gridClass' | 'xlsxClass',
) => {
  const colId = getFieldName(params);
  if (!colId) {
    return false;
  }

  const columnFormat = params.context.columnFormats[colId];
  if (!columnFormat || columnFormat.format?.align !== rule) {
    return false;
  }

  if (scopeClass === 'gridClass') {
    return shouldApplyStyleOptions(params, columnFormat);
  }

  return true;
};

// Enabled when enable_table_printing_improvement_formatting is TRUE and table is undinented.
// Cells in grouped columns use a block layout with nested flex children.
// As a result, textAlign in getCellStyle() does not take effect.
// See common/visualizations/views/agGridReact/customHooks/useAutoGroupAttributes.scss
export const generateGridClasses = () => {
  return {
    cellClassRules: {
      'grouped-cell-center': (params: CellClassParams) =>
        shouldApplyCellAlignmentClass(params, 'center', 'gridClass'),
      'grouped-cell-left': (params: CellClassParams) =>
        shouldApplyCellAlignmentClass(params, 'left', 'gridClass'),
      'grouped-cell-right': (params: CellClassParams) =>
        shouldApplyCellAlignmentClass(params, 'right', 'gridClass'),
    },
  };
};

// Applies CSS class rules used for XLSX (Excel) export styling.
// https://www.ag-grid.com/javascript-data-grid/excel-export-styles/#defining-styles
// shouldGenerateExportCellClasses is undefined in Grid.tsx, but true in ExportGrid.tsx.
const getExportCellProperties = (shouldGenerateExportCellClasses?: boolean) => {
  if (!shouldGenerateExportCellClasses) {
    return {};
  }

  return {
    cellClass: getCellClass,
    cellClassRules: {
      'left-align': (params: CellClassParams) => shouldApplyCellAlignmentClass(params, 'left'),
      'center-align': (params: CellClassParams) => shouldApplyCellAlignmentClass(params, 'center'),
      'right-align': (params: CellClassParams) => shouldApplyCellAlignmentClass(params, 'right'),
      'total-row': isTotalCell,
      'subtotal-row': isSubtotalCell,
    },
  };
};

// Applies cell styling in the AG Grid UI via getCustomCellStyle().
// Note: cellStyle is not exported by default in Excel exports.
// https://www.ag-grid.com/javascript-data-grid/excel-export/?utm_source=chatgpt.com#default-excel-export
const getCellStyle = (params: CellClassParams) => {
  const { showSubTotal, nonStandardAggregations, columnMetadata, columnFormats, currentRowStripeStyle } =
    params.context as CustomAgGridContext;
  const fieldName = getFieldName(params);
  const localColumnMetadata = columnMetadata.find((c) => c.fieldName === fieldName);
  if (!localColumnMetadata || !fieldName) {
    return {};
  }
  const customCellStyle = getCustomCellStyle({
    column: localColumnMetadata,
    nonStandardAggregations: nonStandardAggregations,
    columnFormat: columnFormats[fieldName],
    params,
    showSubTotal,
    currentRowStripeStyle,
  });
  return customCellStyle;
};

const getGridCellProperties = (shouldGenerateGridClasses?: boolean, isIndented?: boolean) => {
  if (!shouldGenerateGridClasses || isIndented) {
    return {};
  }

  return generateGridClasses();
};

export const useAutoGroupAttributes = ({
  columnFormats,
  columns,
  datasetUid,
  domain,
  getUniqueValues,
  handleCustomSortCycle,
  handleGroupColumnPinnedChange,
  handleIndentedHeaderSortIndex,
  headerFormat,
  hierarchies,
  hierarchyConfig,
  isIndented,
  mainMenuItems = [],
  selectedColumns,
  setCurrentSortState,
  setIsCustomSortModalOpen,
  setSelectedColumns,
  shouldGenerateExportCellClasses,
  shouldGenerateGridClasses,
  showCustomSortMenuItems,
  singleAutoColumnWidth,
  vifOrderConfig,
}: AutoGroupAttributesProps) => {
  return useMemo<ColDef>(() => {
    const getMainMenuItems = (params: GetMainMenuItemsParams) => {
      const menuItems = getCustomSortMenuItems({
        getMainMenuParams: params,
        mainMenuItems,
        columns,
        setIsCustomSortModalOpen,
        setSelectedColumns,
        selectedColumns,
        hierarchies,
        getUniqueValues,
        vifOrderConfig,
        columnFormats,
        hierarchyId: hierarchyConfig?.id,
        customSortConfig: hierarchyConfig?.columnSortConfig,
        setCurrentSortState,
        handleCustomSortCycle,
        handleIndentedHeaderSortIndex,
      });

      if (isColumnPinningEnabled() && handleGroupColumnPinnedChange && params.column) {
        const colId = params.column.getColId();
        const groupingConfigs = (hierarchies ?? []).filter((config) => config.isGrouping);
        // The indented single group column combines all grouped fields, so it is pinned when they all are.
        const isPinned =
          colId === GROUP_COLUMN_PREFIX
            ? groupingConfigs.length > 0 && groupingConfigs.every((config) => config.pinned)
            : !!groupingConfigs.find(
                (config) => config.columnName === colId.replace(`${GROUP_COLUMN_PREFIX}-`, ''),
              )?.pinned;

        menuItems.push(
          getPinColumnMenuItem({
            columnName: colId,
            isPinned,
            handleColumnPinnedChange: (pinnedColId, pinned) =>
              handleGroupColumnPinnedChange(pinnedColId, pinned, hierarchyConfig?.id),
          }),
        );
      }

      return menuItems;
    };
    const width = isIndented ? singleAutoColumnWidth : undefined;
    const wrapHeaders = headerFormat?.wrapHeaders ?? false;
    return {
      headerTooltip: 'Group',
      headerValueGetter: (params: HeaderValueGetterParams) => {
        return getHeaderValue(params, isIndented);
      },
      tooltipComponentParams: {
        domain: domain,
        datasetUid: datasetUid,
        isIndented: isIndented,
      },
      headerClass: (params: HeaderClassParams) => {
        return getHeaderClass(params, isIndented);
      },
      tooltipValueGetter: tooltipValueGetterFunction,
      cellRendererParams: {
        innerRenderer: GroupAndTotalRenderer,
        innerRendererParams: {
          datasetUid,
          domain,
          wrapWithDivElement: false,
          customLabels: hierarchyConfig?.customLabels,
        },
      },
      valueGetter: (params: ValueGetterParams) => {
        return getValue(params, isIndented, hierarchyConfig?.customLabels);
      },
      width,
      mainMenuItems: showCustomSortMenuItems ? getMainMenuItems : undefined,
      // Pinning is menu-driven only; block drag-based pinning/unpinning so the grid can't drift from the vif.
      ...(isColumnPinningEnabled() && { lockPinned: true }),
      cellStyle: getCellStyle,
      ...getExportCellProperties(shouldGenerateExportCellClasses),
      ...getGridCellProperties(shouldGenerateGridClasses, isIndented),
      wrapHeaderText: wrapHeaders,
      autoHeaderHeight: wrapHeaders,
    } as ColDef;
    // depend on wrapHeaders only: alignment is read live via context + refreshHeader, so rebuilding here would just reset the grouped column width.
  }, [
    datasetUid,
    domain,
    headerFormat?.wrapHeaders,
    isIndented,
    singleAutoColumnWidth,
    hierarchies,
    columns,
    vifOrderConfig,
    columnFormats,
    hierarchyConfig,
  ]);
};
